import { useContext, useEffect, useState, useMemo } from 'react';
import { Form } from 'react-final-form';
import { FormApi } from 'final-form';
import { useNavigate, useLocation } from 'react-router';
import { Typography } from '@mui/material';
import { ButtonFooter, Column, Spacer, StepsHeader } from '../../components';
import { LicenseData, PaidByEnum } from '../../models/LicenseData';
import { useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { useService } from '@aesop-fables/containr-react';
import { SignUpWizard, signUpWizardKey } from '../../services/signUp';
import {
  PricebookBundle,
  PaymentTypeEnum,
  IntervalTypeEnum,
} from '../../models/PricebookData';
import { LicenseCompartments, licenseStorageKey } from '../../data/license';
import { UpdateSignUpState } from '../../data/signUp/mutations';
import { SelectPricebook } from '../../data/pricebook/mutations';
import { useDeviceType } from '../../hooks/useDeviceType';
import { LicenseAgreementApi, LicenseAgreementData } from '../../api/apis/LicenseAgreementApi';
import { ApiKeys } from '../../api/apis/ApiKeys';
import { useLoading } from '../../hooks/useLoading';
import { SignUpCompartments, signUpStorageKey } from '../../data/signUp';
import { OrgSignUpStateData } from '../../models/SignUpData';
import { Toast } from '../../models/Toast';
import { ToastbarContext } from '../../App';
import HowManyLicenses from './HowManyLicenses';
import PackageOptions from './PackageOptions';
import CancelContract from '../../components/CancelContract';
import { useBasePricebookOptions } from '../../hooks/usePricebookOptions';

const License = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const { setToast } = useContext(ToastbarContext);
  const { isMobile, isTablet } = useDeviceType();

  const updateSignUpState = useMutation(new UpdateSignUpState());
  const selectPricebook = useMutation(new SelectPricebook());

  const appStorage = useAppStorage();
  const licenseDataCache = appStorage.retrieve<LicenseCompartments>(licenseStorageKey);
  const signUpWizard = useService<SignUpWizard>(signUpWizardKey);
  const signUpDataCache = appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
  const licenseAgreementApi = useService<LicenseAgreementApi>(ApiKeys.LicenseAgreement);

  const pricebookData = useBasePricebookOptions();
  const orgSignUpState = useObservable(
    signUpDataCache.observe$<OrgSignUpStateData>('orgSignUpState'),
  );
  const licenseAgreements = useObservable(
    licenseDataCache.observe$<LicenseAgreementData[]>('licenseAgreement'),
  );
  const multipleProductTypesAvailable = useObservable(signUpWizard.multipleProductTypes$) ?? false;
  const multiplePricebooks = useObservable(signUpWizard.multiplePricebooks$) ?? false;
  const { activeStep, totalSteps } = signUpWizard.currentStep(location);

  const [selectedPricebook, setSelectedPricebook] = useState<PricebookBundle | undefined>(
    undefined,
  );
  const [hasSetPricebook, setHasSetPricebook] = useState<boolean>(false);
  const [memberPaidOptions, setMemberPaidOptions] = useState<PricebookBundle[]>([]);
  const [orgPaidOptions, setOrgPaidOptions] = useState<PricebookBundle[]>([]);
  const [initialQuantity, setInitialQuantity] = useState<number | undefined>(0);

  const currentLicense =
    licenseAgreements &&
    licenseAgreements?.find(
      license => license.pricebookId === selectedPricebook?.pricebook.id && license.docStatus,
    );
  const pendingAgreement = currentLicense?.docStatus && !currentLicense?.hasAccepted;
  const activeContract = pendingAgreement || currentLicense?.docStatus;
  const hasActiveAgreement = licenseAgreements?.some(license => Boolean(license.docStatus)) ?? false;
  const product =
    orgSignUpState?.state && orgSignUpState?.state.product
      ? orgSignUpState?.state.product
      : undefined;
  const licenseData: LicenseData =
    orgSignUpState?.state?.licenseData && JSON.parse(orgSignUpState?.state.licenseData);
  const pricebook: PricebookBundle | undefined =
    orgSignUpState?.state?.pricebook && JSON.parse(orgSignUpState?.state.pricebook);
  const meteredPayment = useMemo(
    () => selectedPricebook?.pricebook.paymentType === PaymentTypeEnum.Metered,
    [selectedPricebook?.pricebook.paymentType],
  );
  const activeSubscription = selectedPricebook?.prices.find(
    price => price.intervalType !== IntervalTypeEnum.None,
  );
  const price = (activeSubscription && activeSubscription.price / 100.0) ?? 0;
  const previousStep = signUpWizard.previousStep(location);
  const licenseQuantityRequired = Boolean(
    !meteredPayment &&
      selectedPricebook?.pricebook.productType.includes(product ?? 'x') &&
      selectedPricebook?.pricebook.paidBy === PaidByEnum.Org,
  );

  useEffect(() => {
    if (orgSignUpState?.state) {
      if (typeof product === 'undefined') {
        navigate(previousStep?.route ?? '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSignUpState?.state, previousStep?.route, product]);

  useEffect(() => {
    if (!selectedPricebook?.pricebook.productType.includes(product ?? 'x')) {
      setSelectedPricebook(undefined);
      setInitialQuantity(0);
    } else {
      setInitialQuantity(licenseData?.quantity);
    }
  }, [licenseData?.quantity, product, selectedPricebook?.pricebook.productType]);

  useEffect(() => {
    const updateState = async () => {
      if (currentLicense && !activeContract) {
        await updateSignUpState.action({
          state: {
            ...orgSignUpState?.state,
            licenseData: JSON.stringify({ ...licenseData, quantity: 0 }),
          },
        });
      }
    };
    updateState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeContract, currentLicense]);

  useEffect(() => {
    if (pricebook && !hasSetPricebook && pricebookData) {
      if (pricebookData.some(pb => pb.pricebook.id === pricebook.pricebook.id)) {
        setSelectedPricebook(pricebook);
      }
      setHasSetPricebook(true);
    }
  }, [pricebook, hasSetPricebook, pricebookData]);

  useEffect(() => {
    const getPricebookOptions = async () => {
      if (pricebookData) {
        const pricebooks = pricebookData.filter(bundle =>
          bundle.pricebook.productType.includes(product ?? ''),
        );
        const member = pricebooks.filter(bundle => bundle.pricebook.paidBy === PaidByEnum.User);
        const org = pricebooks.filter(bundle => bundle.pricebook.paidBy === PaidByEnum.Org);

        setMemberPaidOptions(member);
        setOrgPaidOptions(org);

        if (pricebooks.length === 1) {
          const singlePricebook = pricebooks[0];
          setSelectedPricebook(singlePricebook);

          if (orgSignUpState?.state) {
            await updateSignUpState.action({
              state: {
                ...orgSignUpState.state,
                pricebook: JSON.stringify(singlePricebook),
              },
            });
          }
        }
      }
    };

    getPricebookOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, pricebookData]);

  const onSubmit = async (values: LicenseData, form: FormApi<LicenseData>) => {
    if (!selectedPricebook) return;

    if (currentLicense && form.getState().pristine) {
      const next = signUpWizard.nextStep(location);
      navigate(next.route);
      return;
    }

    try {
      setLoading(true);
      const licenseData: LicenseData = {
        ...values,
        paymentParty: selectedPricebook.pricebook.paidBy,
        package: selectedPricebook.pricebook.productType,
      };
      if (licenseData.paymentParty === PaidByEnum.User) {
        licenseData.quantity = undefined;
      } else {
        if (typeof values.quantity === 'string') {
          licenseData.quantity = parseInt(values.quantity);
        }
        licenseData.cost = price;
      }
      await selectPricebook.action(selectedPricebook);

      const generatedLicense = (await licenseAgreementApi.generate(selectedPricebook.pricebook.id))
        .data;
      await updateSignUpState.action({
        lastCompletedStep: signUpWizard.getCurrent(location),
        state: {
          ...orgSignUpState?.state,
          stripeSession: JSON.stringify(null),
          licenseData: JSON.stringify({ ...licenseData, ...generatedLicense }),
          pricebook: JSON.stringify(selectedPricebook),
        },
      });
      await licenseDataCache.reloadAll();
      const next = signUpWizard.nextStep(location);
      navigate(next.route);
    } catch (error) {
      console.error('Failed to select pricebook with id: ' + selectedPricebook.pricebook.id);
      setToast(
        new Toast({
          message: `Error selecting package`,
          severity: 'error',
          open: true,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const navBack = () => {
    navigate(previousStep?.route ?? '');
  };

  if (!product) return null;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        quantity: initialQuantity,
      }}>
      {({ handleSubmit, values }) => (
        <Column style={{ margin: isMobile || isTablet ? '0px 20px' : '0px 100px' }}>
          <Spacer height='xs' />
          <StepsHeader
            activeStep={activeStep}
            totalSteps={totalSteps}
            headerSteps={
              multipleProductTypesAvailable ? 'onboardingMultipleProducts' : 'onboarding'
            }
          />
          <Spacer height='xs' />
          <Typography variant='h1' color='secondary'>
            Step {activeStep}: Licenses
          </Typography>
          <Spacer height='sm' />
          <Typography variant='p18SemiBold' color='secondary'>
            {multiplePricebooks
              ? 'Which package would you like to add for your members?'
              : "Your organization's exclusive package"}
          </Typography>
          <Spacer height='xs' />
          <PackageOptions
            memberPaidOptions={memberPaidOptions}
            orgPaidOptions={orgPaidOptions}
            selectedPricebook={selectedPricebook}
            setSelectedPricebook={setSelectedPricebook}
            licenseAgreements={licenseAgreements}
            signUpState={orgSignUpState?.state}
            updateSignUpState={updateSignUpState}
            multiplePricebooks={multiplePricebooks}
          />
          {licenseQuantityRequired && (
            <HowManyLicenses values={values} cost={price} disabled={hasActiveAgreement} />
          )}

          <ButtonFooter
            primaryButton={{
              label: 'Next: Review',
              onClick: handleSubmit,
              disabled:
                (multiplePricebooks && !selectedPricebook) ||
                (licenseQuantityRequired && !values?.quantity),
            }}
            secondaryButton={{
              label: 'Back',
              onClick: navBack,
              showCondition: true,
            }}
            tertiaryButton={{
              showCondition: activeContract,
              component: <CancelContract />,
            }}
          />
        </Column>
      )}
    </Form>
  );
};

export default License;
